// ============================================================================
// Site Data Context
// Shared state management for site data across SitesDashboard and SiteManagementCard
// ============================================================================

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  ReactNode,
} from 'react';

import {
  SiteDataState,
  SiteDataAction,
  SiteDataContextValue,
  SiteListItem,
  SiteDetails,
  SiteFilters,
  SiteSortKey,
  StoreConfig,
} from '../types/siteData';

import { Site, SiteBlackout } from '../types/site';

import { siteService } from '../services/siteService';
import { getAllActiveSiteBlackouts } from '../services/blackoutService';
import { getStorePricing, getStoreTaxes, getStoreCharges } from '../services/configService';
import {
  resolveEffectivePricing,
  resolveEffectiveOverageCharges,
  resolveEffectiveFees,
} from '../utils/effectiveValues';
import { authService } from '../services/auth';

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 30;

// ============================================================================
// Initial State
// ============================================================================

const initialState: SiteDataState = {
  // Store configuration
  storeConfig: null,
  storeConfigLoading: false,
  storeConfigError: null,
  storeConfigLoaded: false,

  // Sites list
  sites: [],
  sitesLoading: false,
  sitesError: null,
  sitesLoaded: false,

  // Site details cache
  siteDetailsCache: new Map(),
  loadingDetails: new Set(),

  // Pagination
  pagination: {
    page: 1,
    pageSize: PAGE_SIZE,
    hasMore: true,
    totalCount: 0,
  },

  // Filters (no hookup filter per design)
  filters: {
    search: '',
    type_id: undefined,
    section_id: undefined,
  },

  // Sort
  sort: {
    key: 'alias',
    direction: 'asc',
  },
};

// ============================================================================
// Reducer
// ============================================================================

function siteDataReducer(state: SiteDataState, action: SiteDataAction): SiteDataState {
  switch (action.type) {
    // Store config actions
    case 'STORE_CONFIG_LOADING':
      return {
        ...state,
        storeConfigLoading: true,
        storeConfigError: null,
      };

    case 'STORE_CONFIG_LOADED':
      return {
        ...state,
        storeConfig: action.payload,
        storeConfigLoading: false,
        storeConfigError: null,
        storeConfigLoaded: true,
      };

    case 'STORE_CONFIG_ERROR':
      return {
        ...state,
        storeConfigLoading: false,
        storeConfigError: action.payload,
      };

    // Sites list actions
    case 'SITES_LOADING':
      return {
        ...state,
        sitesLoading: true,
        sitesError: null,
      };

    case 'SITES_LOADED':
      return {
        ...state,
        sites: action.payload.sites,
        sitesLoading: false,
        sitesError: null,
        sitesLoaded: true,
        pagination: {
          ...state.pagination,
          page: 1,
          totalCount: action.payload.totalCount,
          hasMore: action.payload.sites.length >= PAGE_SIZE,
        },
      };

    case 'SITES_LOADING_MORE':
      return {
        ...state,
        sitesLoading: true,
      };

    case 'SITES_PAGE_LOADED': {
      // Update existing sites with new pricing data (don't append - sites already exist)
      const updatedSitesMap = new Map(action.payload.sites.map((s: SiteListItem) => [s.id, s]));
      const updatedSites = state.sites.map((site) => {
        const updated = updatedSitesMap.get(site.id);
        return updated || site;
      });
      return {
        ...state,
        sites: updatedSites,
        sitesLoading: false,
        pagination: {
          ...state.pagination,
          page: state.pagination.page + 1,
          hasMore: action.payload.hasMore,
        },
      };
    }

    case 'SITES_ERROR':
      return {
        ...state,
        sitesLoading: false,
        sitesError: action.payload,
      };

    // Site details actions
    case 'SITE_DETAILS_LOADING': {
      const newLoading = new Set(state.loadingDetails);
      newLoading.add(action.payload);
      return {
        ...state,
        loadingDetails: newLoading,
      };
    }

    case 'SITE_DETAILS_LOADED': {
      const newCache = new Map(state.siteDetailsCache);
      newCache.set(action.payload.siteId, action.payload.details);
      const newLoading = new Set(state.loadingDetails);
      newLoading.delete(action.payload.siteId);
      return {
        ...state,
        siteDetailsCache: newCache,
        loadingDetails: newLoading,
      };
    }

    case 'SITE_DETAILS_ERROR': {
      const newLoading = new Set(state.loadingDetails);
      newLoading.delete(action.payload.siteId);
      return {
        ...state,
        loadingDetails: newLoading,
      };
    }

    // Cache invalidation actions
    case 'INVALIDATE_SITE_DETAILS': {
      const newCache = new Map(state.siteDetailsCache);
      newCache.delete(action.payload);
      return {
        ...state,
        siteDetailsCache: newCache,
      };
    }

    case 'INVALIDATE_ALL_SITE_DETAILS':
      return {
        ...state,
        siteDetailsCache: new Map(),
      };

    case 'INVALIDATE_SITES':
      return {
        ...state,
        sites: [],
        sitesLoaded: false,
        siteDetailsCache: new Map(),
        pagination: {
          ...state.pagination,
          page: 1,
          hasMore: true,
          totalCount: 0,
        },
      };

    case 'INVALIDATE_STORE_CONFIG':
      return {
        ...state,
        storeConfig: null,
        storeConfigLoaded: false,
      };

    // CRUD notification actions
    case 'SITE_CREATED':
      // Just invalidate to trigger a fresh load
      return {
        ...state,
        sitesLoaded: false,
      };

    case 'SITE_UPDATED': {
      // Invalidate the specific site's details cache
      const newCache = new Map(state.siteDetailsCache);
      newCache.delete(action.payload);
      return {
        ...state,
        siteDetailsCache: newCache,
        sitesLoaded: false, // Force reload to get updated data
      };
    }

    case 'SITE_DELETED': {
      // Remove from sites list and cache
      const newCache = new Map(state.siteDetailsCache);
      newCache.delete(action.payload);
      return {
        ...state,
        sites: state.sites.filter((s) => s.id !== action.payload),
        siteDetailsCache: newCache,
        pagination: {
          ...state.pagination,
          totalCount: Math.max(0, state.pagination.totalCount - 1),
        },
      };
    }

    // Filter/sort actions
    case 'SET_FILTERS':
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
        // Reset pagination when filters change
        pagination: {
          ...state.pagination,
          page: 1,
        },
      };

    case 'CLEAR_FILTERS':
      return {
        ...state,
        filters: {
          search: '',
          type_id: undefined,
          section_id: undefined,
        },
        pagination: {
          ...state.pagination,
          page: 1,
        },
      };

    case 'SET_SORT': {
      const newDirection =
        state.sort.key === action.payload && state.sort.direction === 'asc' ? 'desc' : 'asc';
      return {
        ...state,
        sort: {
          key: action.payload,
          direction: newDirection,
        },
      };
    }

    case 'RESET_PAGINATION':
      return {
        ...state,
        pagination: {
          ...state.pagination,
          page: 1,
        },
      };

    default:
      return state;
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Apply filters to sites list
 */
function applyFilters(sites: SiteListItem[], filters: SiteFilters): SiteListItem[] {
  return sites.filter((site) => {
    // Search filter (alias, description, type_name, section_name)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch =
        site.alias.toLowerCase().includes(searchLower) ||
        (site.description && site.description.toLowerCase().includes(searchLower)) ||
        (site.type_name && site.type_name.toLowerCase().includes(searchLower)) ||
        (site.section_name && site.section_name.toLowerCase().includes(searchLower));
      if (!matchesSearch) return false;
    }

    // Type filter
    if (filters.type_id !== undefined && site.type_id !== filters.type_id) {
      return false;
    }

    // Section filter
    if (filters.section_id !== undefined && site.site_section_id !== filters.section_id) {
      return false;
    }

    // Status filter
    if (filters.status !== undefined && site.status !== filters.status) {
      return false;
    }

    return true;
  });
}

/**
 * Natural/alphanumeric comparison for strings with numbers
 * e.g., "Site1", "Site2", "Site10" sorts correctly instead of "Site1", "Site10", "Site2"
 */
function naturalCompare(a: string, b: string): number {
  const regex = /(\d+)|(\D+)/g;
  const aParts = a.match(regex) || [];
  const bParts = b.match(regex) || [];

  for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
    const aPart = aParts[i] || '';
    const bPart = bParts[i] || '';

    // If both parts are numeric, compare as numbers
    const aNum = parseInt(aPart, 10);
    const bNum = parseInt(bPart, 10);

    if (!isNaN(aNum) && !isNaN(bNum)) {
      if (aNum !== bNum) {
        return aNum - bNum;
      }
    } else {
      // Otherwise compare as strings (case-insensitive)
      const cmp = aPart.toLowerCase().localeCompare(bPart.toLowerCase());
      if (cmp !== 0) {
        return cmp;
      }
    }
  }

  return 0;
}

/**
 * Apply sorting to sites list
 */
function applySort(
  sites: SiteListItem[],
  sort: { key: SiteSortKey; direction: 'asc' | 'desc' }
): SiteListItem[] {
  const sorted = [...sites];
  const direction = sort.direction === 'asc' ? 1 : -1;

  sorted.sort((a, b) => {
    let comparison = 0;

    switch (sort.key) {
      case 'alias':
        comparison = naturalCompare(a.alias, b.alias);
        break;
      case 'type_name':
        comparison = naturalCompare(a.type_name || '', b.type_name || '');
        break;
      case 'section_name':
        comparison = naturalCompare(a.section_name || '', b.section_name || '');
        break;
      case 'effective_rate':
        comparison = (a.effectivePricing?.price || 0) - (b.effectivePricing?.price || 0);
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      default:
        comparison = 0;
    }

    return comparison * direction;
  });

  return sorted;
}

/**
 * Transform a Site to SiteListItem with derived fields
 */
function transformSiteToListItem(
  site: Site,
  storeConfig: StoreConfig,
  sitePricing: any[],
  activeBlackouts?: SiteBlackout[]
): SiteListItem {
  // Get type name
  const siteType = storeConfig.siteTypes.find((t) => t.id === site.type_id);
  const type_name = siteType?.name;

  // Get section name
  const section = storeConfig.sections.find((s) => s.id === site.site_section_id);
  const section_name = section?.name;

  // Determine status
  let status: 'available' | 'seasonal' | 'blocked' = 'available';
  if (activeBlackouts && activeBlackouts.some((b) => b.site_id === site.id && b.active)) {
    status = 'blocked';
  }
  if (status === 'available' && site.seasonal) {
    status = 'seasonal';
  }

  // Calculate effective pricing
  const effectivePricing = resolveEffectivePricing(
    site.type_id,
    sitePricing,
    storeConfig.storePricing
  );

  return {
    id: site.id,
    store_id: site.store_id,
    alias: site.alias,
    description: site.description,
    type_id: site.type_id,
    site_section_id: site.site_section_id,
    max_length: site.max_length,
    seasonal: site.seasonal,
    type_name,
    section_name,
    status,
    effectivePricing,
    sitePricing,
  };
}

// ============================================================================
// Context
// ============================================================================

const SiteDataContext = createContext<SiteDataContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface SiteDataProviderProps {
  children: ReactNode;
}

export function SiteDataProvider({ children }: SiteDataProviderProps) {
  const [state, dispatch] = useReducer(siteDataReducer, initialState);

  // ============================================================================
  // Store Config Loading
  // ============================================================================

  const loadStoreConfig = useCallback(async () => {
    // Skip if already loaded or loading
    if (state.storeConfigLoaded || state.storeConfigLoading) {
      return;
    }

    dispatch({ type: 'STORE_CONFIG_LOADING' });

    try {
      const storeId = authService.getStoreId();

      // Load all store config in parallel
      // Note: getStoreHookupTypes takes activeOnly?: boolean, not storeId
      const [
        siteTypes,
        sections,
        hookupTypes,
        storePricing,
        storeTaxes,
        storeOverageFees,
        storeCharges,
      ] = await Promise.all([
        siteService.getStoreSiteTypes(storeId),
        siteService.getStoreSiteSections(storeId),
        siteService.getStoreHookupTypes(), // Auto-filters by authenticated user's store
        getStorePricing(storeId),
        getStoreTaxes(storeId),
        siteService.getStoreOverageFees(storeId),
        getStoreCharges(storeId),
      ]);

      const config: StoreConfig = {
        siteTypes,
        sections,
        hookupTypes,
        storePricing,
        storeTaxes,
        storeOverageFees,
        storeCharges,
      };

      dispatch({ type: 'STORE_CONFIG_LOADED', payload: config });
    } catch (error) {
      console.error('Failed to load store config:', error);
      dispatch({
        type: 'STORE_CONFIG_ERROR',
        payload: error instanceof Error ? error.message : 'Failed to load store configuration',
      });
    }
  }, [state.storeConfigLoaded, state.storeConfigLoading]);

  // Auto-load store config on mount (once per session)
  const storeConfigLoadAttempted = useRef(false);
  useEffect(() => {
    if (!storeConfigLoadAttempted.current && !state.storeConfigLoaded && !state.storeConfigLoading) {
      storeConfigLoadAttempted.current = true;
      loadStoreConfig();
    }
  }, [loadStoreConfig, state.storeConfigLoaded, state.storeConfigLoading]);

  // ============================================================================
  // Sites Loading
  // ============================================================================

  const loadSites = useCallback(async () => {
    // Skip if already loaded or loading
    if (state.sitesLoaded || state.sitesLoading) {
      return;
    }

    // Require store config to be loaded first
    // Components should wait for storeConfigLoaded before calling loadSites
    if (!state.storeConfig || !state.storeConfigLoaded) {
      console.warn('Store config not available yet, sites will load when config is ready');
      return;
    }

    dispatch({ type: 'SITES_LOADING' });

    try {
      const storeId = authService.getStoreId();

      // Load all sites and active blackouts in parallel
      const [allSites, activeBlackouts] = await Promise.all([
        siteService.getSites(storeId),
        getAllActiveSiteBlackouts().catch(() => [] as SiteBlackout[]),
      ]);

      // Load pricing for first page of sites
      const firstPageSites = allSites.slice(0, PAGE_SIZE);
      const sitePricingMap = new Map<number, any[]>();

      // Load pricing for each site in the first page (in batches of 5)
      const BATCH_SIZE = 5;
      for (let i = 0; i < firstPageSites.length; i += BATCH_SIZE) {
        const batch = firstPageSites.slice(i, i + BATCH_SIZE);
        const pricingResults = await Promise.all(
          batch.map((site) => siteService.getSitePricing(site.id).catch(() => []))
        );
        batch.forEach((site, idx) => {
          sitePricingMap.set(site.id, pricingResults[idx]);
        });
      }

      // Transform sites to list items
      const siteListItems: SiteListItem[] = allSites.map((site) => {
        const sitePricing = sitePricingMap.get(site.id) || [];
        return transformSiteToListItem(site, state.storeConfig!, sitePricing, activeBlackouts);
      });

      dispatch({
        type: 'SITES_LOADED',
        payload: {
          sites: siteListItems,
          totalCount: allSites.length,
        },
      });
    } catch (error) {
      console.error('Failed to load sites:', error);
      dispatch({
        type: 'SITES_ERROR',
        payload: error instanceof Error ? error.message : 'Failed to load sites',
      });
    }
  }, [state.sitesLoaded, state.sitesLoading, state.storeConfig, state.storeConfigLoaded]);

  // Auto-load sites when store config is ready
  const sitesLoadAttempted = useRef(false);
  useEffect(() => {
    if (state.storeConfigLoaded && !sitesLoadAttempted.current && !state.sitesLoaded && !state.sitesLoading) {
      sitesLoadAttempted.current = true;
      loadSites();
    }
  }, [state.storeConfigLoaded, state.sitesLoaded, state.sitesLoading, loadSites]);

  const loadMoreSites = useCallback(async () => {
    // Skip if already loading or no more sites
    if (state.sitesLoading || !state.pagination.hasMore) {
      return;
    }

    // Skip if store config not loaded
    if (!state.storeConfig) {
      return;
    }

    dispatch({ type: 'SITES_LOADING_MORE' });

    try {
      // Get the next page of sites that need pricing loaded
      const startIndex = state.pagination.page * PAGE_SIZE;
      const endIndex = startIndex + PAGE_SIZE;
      const sitesPage = state.sites.slice(startIndex, endIndex);

      // Load pricing for sites that don't have it yet
      const sitePricingMap = new Map<number, any[]>();
      const BATCH_SIZE = 5;

      for (let i = 0; i < sitesPage.length; i += BATCH_SIZE) {
        const batch = sitesPage.slice(i, i + BATCH_SIZE);
        const pricingResults = await Promise.all(
          batch.map((site) => siteService.getSitePricing(site.id).catch(() => []))
        );
        batch.forEach((site, idx) => {
          sitePricingMap.set(site.id, pricingResults[idx]);
        });
      }

      // Update sites with pricing
      const updatedSites: SiteListItem[] = sitesPage.map((site) => {
        const sitePricing = sitePricingMap.get(site.id) || [];
        if (sitePricing.length > 0 || !site.effectivePricing) {
          const effectivePricing = resolveEffectivePricing(
            site.type_id,
            sitePricing,
            state.storeConfig!.storePricing
          );
          return {
            ...site,
            effectivePricing,
            sitePricing,
          };
        }
        return site;
      });

      dispatch({
        type: 'SITES_PAGE_LOADED',
        payload: {
          sites: updatedSites,
          hasMore: endIndex < state.sites.length,
        },
      });
    } catch (error) {
      console.error('Failed to load more sites:', error);
      dispatch({
        type: 'SITES_ERROR',
        payload: error instanceof Error ? error.message : 'Failed to load more sites',
      });
    }
  }, [state.sitesLoading, state.pagination, state.sites, state.storeConfig]);

  const refreshSites = useCallback(async () => {
    dispatch({ type: 'INVALIDATE_SITES' });
    // Will trigger loadSites on next render
  }, []);

  // ============================================================================
  // Site Details Loading (Lazy)
  // ============================================================================

  const loadSiteDetails = useCallback(
    async (siteId: number): Promise<SiteDetails | undefined> => {
      // Return cached if available
      const cached = state.siteDetailsCache.get(siteId);
      if (cached) {
        return cached;
      }

      // Skip if already loading
      if (state.loadingDetails.has(siteId)) {
        return undefined;
      }

      // Skip if store config not loaded
      if (!state.storeConfig) {
        console.warn('Store config not available, cannot load site details');
        return undefined;
      }

      dispatch({ type: 'SITE_DETAILS_LOADING', payload: siteId });

      try {
        // Load hookups, overage fees, and charges in parallel
        const [hookups, overageFees, charges] = await Promise.all([
          siteService.getSiteHookups(siteId),
          siteService.getSiteOverageFees(siteId),
          siteService.getSiteCharges(siteId),
        ]);

        // Compute effective values
        const effectiveOverageCharges = resolveEffectiveOverageCharges(
          overageFees,
          state.storeConfig.storeOverageFees
        );

        const effectiveFees = resolveEffectiveFees(charges, state.storeConfig.storeCharges);

        const details: SiteDetails = {
          hookups,
          overageFees,
          charges,
          effectiveOverageCharges,
          effectiveFees,
        };

        dispatch({ type: 'SITE_DETAILS_LOADED', payload: { siteId, details } });

        return details;
      } catch (error) {
        console.error(`Failed to load site details for site ${siteId}:`, error);
        dispatch({ type: 'SITE_DETAILS_ERROR', payload: { siteId, error: String(error) } });
        return undefined;
      }
    },
    [state.siteDetailsCache, state.loadingDetails, state.storeConfig]
  );

  const getSiteDetails = useCallback(
    (siteId: number): SiteDetails | undefined => {
      return state.siteDetailsCache.get(siteId);
    },
    [state.siteDetailsCache]
  );

  const isSiteDetailsLoading = useCallback(
    (siteId: number): boolean => {
      return state.loadingDetails.has(siteId);
    },
    [state.loadingDetails]
  );

  // ============================================================================
  // Filter/Sort Actions
  // ============================================================================

  const setFilters = useCallback((filters: Partial<SiteFilters>) => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  }, []);

  const setSort = useCallback((key: SiteSortKey) => {
    dispatch({ type: 'SET_SORT', payload: key });
  }, []);

  const clearFilters = useCallback(() => {
    dispatch({ type: 'CLEAR_FILTERS' });
  }, []);

  // ============================================================================
  // Cache Management
  // ============================================================================

  const invalidateSiteDetails = useCallback((siteId: number) => {
    dispatch({ type: 'INVALIDATE_SITE_DETAILS', payload: siteId });
  }, []);

  const invalidateAllSiteDetails = useCallback(() => {
    dispatch({ type: 'INVALIDATE_ALL_SITE_DETAILS' });
  }, []);

  const invalidateSiteList = useCallback(() => {
    dispatch({ type: 'INVALIDATE_SITES' });
  }, []);

  // ============================================================================
  // CRUD Notifications
  // ============================================================================

  const onSiteCreated = useCallback((site: Site) => {
    dispatch({ type: 'SITE_CREATED', payload: site });
  }, []);

  const onSiteUpdated = useCallback((siteId: number) => {
    dispatch({ type: 'SITE_UPDATED', payload: siteId });
  }, []);

  const onSiteDeleted = useCallback((siteId: number) => {
    dispatch({ type: 'SITE_DELETED', payload: siteId });
  }, []);

  // ============================================================================
  // Computed Values
  // ============================================================================

  const { displayedSites, filteredCount } = useMemo(() => {
    // Apply filters
    const filtered = applyFilters(state.sites, state.filters);

    // Apply sort
    const sorted = applySort(filtered, state.sort);

    // Apply pagination
    const pageEnd = state.pagination.page * state.pagination.pageSize;
    const displayed = sorted.slice(0, pageEnd);

    return {
      displayedSites: displayed,
      filteredCount: filtered.length,
    };
  }, [state.sites, state.filters, state.sort, state.pagination]);

  // ============================================================================
  // Context Value
  // ============================================================================

  const value = useMemo<SiteDataContextValue>(
    () => ({
      state,
      displayedSites,
      filteredCount,
      loadStoreConfig,
      loadSites,
      loadMoreSites,
      refreshSites,
      loadSiteDetails,
      getSiteDetails,
      isSiteDetailsLoading,
      setFilters,
      setSort,
      clearFilters,
      invalidateSiteDetails,
      invalidateAllSiteDetails,
      invalidateSiteList,
      onSiteCreated,
      onSiteUpdated,
      onSiteDeleted,
    }),
    [
      state,
      displayedSites,
      filteredCount,
      loadStoreConfig,
      loadSites,
      loadMoreSites,
      refreshSites,
      loadSiteDetails,
      getSiteDetails,
      isSiteDetailsLoading,
      setFilters,
      setSort,
      clearFilters,
      invalidateSiteDetails,
      invalidateAllSiteDetails,
      invalidateSiteList,
      onSiteCreated,
      onSiteUpdated,
      onSiteDeleted,
    ]
  );

  return <SiteDataContext.Provider value={value}>{children}</SiteDataContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

export function useSiteData(): SiteDataContextValue {
  const context = useContext(SiteDataContext);
  if (!context) {
    throw new Error('useSiteData must be used within a SiteDataProvider');
  }
  return context;
}

export default SiteDataContext;
